// Enhanced JWT Authentication Service with Auto-logout
import { toast } from "react-toastify";
import { API_CONFIG } from "../config/api.config";

class AuthService {
  constructor() {
    this.TOKEN_KEY = "trivana_jwt_token";
    this.USER_KEY = "trivana_user";
    this.TOKEN_EXPIRY_KEY = "trivana_token_expiry";
    this.REFRESH_TOKEN_KEY = "trivana_refresh_token";
    this.INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes default
    this.WARNING_TIME = 5 * 60 * 1000; // Show warning 5 minutes before logout

    this.inactivityTimer = null;
    this.warningTimer = null;
    this.lastActivity = Date.now();

    // Bind methods to preserve context
    this.resetInactivityTimer = this.resetInactivityTimer.bind(this);
    this.handleUserActivity = this.handleUserActivity.bind(this);

    // Initialize activity tracking
    this.initializeActivityTracking();
  }

  // Initialize activity tracking across the app
  initializeActivityTracking() {
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];

    events.forEach((event) => {
      document.addEventListener(event, this.handleUserActivity, {
        passive: true,
      });
    });

    // Check token validity on page load
    this.validateTokenOnLoad();
  }

  // Handle user activity
  handleUserActivity() {
    this.lastActivity = Date.now();
    this.resetInactivityTimer();
  }

  // Reset inactivity timer
  resetInactivityTimer() {
    // Clear existing timers
    if (this.inactivityTimer) clearTimeout(this.inactivityTimer);
    if (this.warningTimer) clearTimeout(this.warningTimer);

    // Only set timers if user is logged in
    if (this.isAuthenticated()) {
      // Set warning timer (5 minutes before logout)
      this.warningTimer = setTimeout(() => {
        this.showLogoutWarning();
      }, this.INACTIVITY_TIMEOUT - this.WARNING_TIME);

      // Set logout timer
      this.inactivityTimer = setTimeout(() => {
        this.handleInactivityLogout();
      }, this.INACTIVITY_TIMEOUT);
    }
  }

  // Show logout warning
  showLogoutWarning() {
    const extendSession = () => {
      this.resetInactivityTimer();
      toast.dismiss("logout-warning");
      toast.success("Session extended successfully!");
    };

    toast.warn(
      <div>
        <p>You'll be logged out in 5 minutes due to inactivity.</p>
        <button
          onClick={extendSession}
          className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Extend Session
        </button>
      </div>,
      {
        toastId: "logout-warning",
        autoClose: false,
        closeOnClick: false,
        draggable: false,
      }
    );
  }

  // Handle inactivity logout
  handleInactivityLogout() {
    toast.dismiss("logout-warning");
    this.logout();
    toast.error("You have been logged out due to inactivity.");

    // Redirect to login page
    if (window.location.pathname !== "/login") {
      window.location.href = "/login";
    }
  }

  // Validate token on page load
  validateTokenOnLoad() {
    const token = this.getToken();
    if (token && this.isTokenExpired(token)) {
      this.logout();
      toast.error("Your session has expired. Please login again.");
    } else if (token) {
      this.resetInactivityTimer();
    }
  }

  // Store JWT token with expiration
  setToken(token, expiresIn = 3600) {
    // Default 1 hour
    const expirationTime = Date.now() + expiresIn * 1000;

    localStorage.setItem(this.TOKEN_KEY, token);
    localStorage.setItem(this.TOKEN_EXPIRY_KEY, expirationTime.toString());

    this.resetInactivityTimer();
  }

  // Get JWT token
  getToken() {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  // Check if token is expired
  isTokenExpired(token = null) {
    const tokenToCheck = token || this.getToken();
    if (!tokenToCheck) return true;

    const expiry = localStorage.getItem(this.TOKEN_EXPIRY_KEY);
    if (!expiry) return true;

    return Date.now() > parseInt(expiry);
  }

  // Parse JWT payload
  parseJWTPayload(token = null) {
    try {
      const tokenToParse = token || this.getToken();
      if (!tokenToParse) return null;

      const base64Url = tokenToParse.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );

      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error("Error parsing JWT:", error);
      return null;
    }
  }

  // Store user data
  setUser(userData) {
    localStorage.setItem(this.USER_KEY, JSON.stringify(userData));
  }

  // Get user data
  getUser() {
    const userData = localStorage.getItem(this.USER_KEY);
    return userData ? JSON.parse(userData) : null;
  }

  // Check if user is authenticated
  isAuthenticated() {
    const token = this.getToken();
    return token && !this.isTokenExpired(token);
  }

  // Get authorization header
  getAuthHeader() {
    const token = this.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  // Logout and clear all data
  logout() {
    // Clear timers
    if (this.inactivityTimer) clearTimeout(this.inactivityTimer);
    if (this.warningTimer) clearTimeout(this.warningTimer);

    // Clear storage
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.TOKEN_EXPIRY_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);

    // Clear any pending toasts
    toast.dismiss("logout-warning");
  }

  // Set inactivity timeout (in milliseconds)
  setInactivityTimeout(milliseconds) {
    this.INACTIVITY_TIMEOUT = milliseconds;
    this.WARNING_TIME = Math.min(this.WARNING_TIME, milliseconds / 6); // Warning at 1/6 of timeout
    if (this.isAuthenticated()) {
      this.resetInactivityTimer();
    }
  }

  // Get time until logout (in milliseconds)
  getTimeUntilLogout() {
    if (!this.isAuthenticated()) return 0;

    const timeSinceLastActivity = Date.now() - this.lastActivity;
    const timeRemaining = this.INACTIVITY_TIMEOUT - timeSinceLastActivity;

    return Math.max(0, timeRemaining);
  }

  // Cleanup method for component unmounting
  cleanup() {
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];

    events.forEach((event) => {
      document.removeEventListener(event, this.handleUserActivity);
    });

    if (this.inactivityTimer) clearTimeout(this.inactivityTimer);
    if (this.warningTimer) clearTimeout(this.warningTimer);
  }

  // Check if user exists (for duplicate prevention)
  async checkUserExists(email, phone = null) {
    try {
      const response = await fetch(`${API_CONFIG.AUTH_URL}/check-user`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, phone }),
      });

      if (response.ok) {
        const data = await response.json();
        return data.exists || false;
      }
      return false;
    } catch (error) {
      console.error("Error checking user existence:", error);
      return false;
    }
  }
}

// Create singleton instance
const authService = new AuthService();

export default authService;

// Export individual methods for convenience
export const {
  setToken,
  getToken,
  setUser,
  getUser,
  isAuthenticated,
  getAuthHeader,
  logout,
  isTokenExpired,
  parseJWTPayload,
  setInactivityTimeout,
  getTimeUntilLogout,
  checkUserExists,
} = authService;
